/**
 * @module  public/js/services/tenantResolver.js
 * @class   TenantResolver
 * @summary Resolve the active tenant ID with simple caching.
 *
 * @description
 * Reads the tenant identifier from the provided AuthService synchronously when
 * possible. If the ID is not immediately available it will wait for the user to
 * authenticate exactly once and cache the resolved value. Subsequent calls are
 * synchronous and never trigger additional network requests.
 */
export default class TenantResolver {
  /** @type {import('../services/authservice.js').default} */
  #auth;
  #cachedId = null;
  #pending = null;

  /**
   * @param {import('../services/authservice.js').default} auth
   */
  constructor(auth) {
    this.#auth = auth;
  }

  /**
   * Return the cached tenant ID if present or attempt to read it synchronously
   * from the auth service. Does not trigger network calls.
   * @returns {string|null}
   */
  getTenantIdSync() {
    if (this.#cachedId) return this.#cachedId;
    const tid = this.#auth.currentUser?.tenantId ?? null;
    if (tid) this.#cachedId = tid;
    return this.#cachedId;
  }

  /**
   * Resolve the tenant ID, awaiting login once when necessary. The resolved
   * value is cached for subsequent synchronous access.
   * @returns {Promise<string|null>}
   */
  async resolveTenantId() {
    const sync = this.getTenantIdSync();
    if (sync) return sync;
    if (!this.#pending) {
      this.#pending = this.#auth
        .waitForLogin()
        .then((u) => {
          const tid = u?.tenantId ?? null;
          if (tid) this.#cachedId = tid;
          return tid;
        })
        .finally(() => {
          this.#pending = null;
        });
    }
    return this.#pending;
  }
}
